import React, { Fragment, useRef, useState, useContext, useEffect } from 'react';
import { AuthContext } from '../contexts/auth';
import { Dialog, Transition } from '@headlessui/react';
import { PencilSquareIcon, CheckIcon, TrashIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { updateJustificationRequestStatus } from '../services/api_justification_requests';
import Swal from 'sweetalert2';
import moment from 'moment';

const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const dateTime = new Date(dateTimeString);
    const hours = dateTime.getHours().toString().padStart(2, '0');
    const minutes = dateTime.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

function formatJustificationEnd(datetimeFrom, datetimeTo) {
    const from = moment(datetimeFrom);
    const to = moment(datetimeTo);

    // Crea una copia di 'from' per evitare di modificarlo
    const fromCopy = moment(from);

    // Verifica se il datetime_to è esattamente a mezzanotte del giorno successivo
    const isNextDayMidnight =
        to.isSame(fromCopy.add(1, 'days').startOf('day'), 'day') && to.format('HH:mm') === '00:00';
    
    // Se datetime_to è a mezzanotte del giorno successivo, mantieni la stessa data di inizio per il "giorno" di fine
    const endDate = isNextDayMidnight ? from.format('DD/MM/YYYY') : to.format('DD/MM/YYYY');
    const endTime = isNextDayMidnight ? '24:00' : to.format('HH:mm');
    
    return {
        date: endDate,
        time: endTime,
    };
}

// function formatEndDate(datetimeTo) {
//     const time = moment(datetimeTo).format('HH:mm');

//     if (time === '00:00') {
//         return moment(datetimeTo).subtract(1, 'day').format('DD/MM/YYYY');
//     }

//     return moment(datetimeTo).format('DD/MM/YYYY');
// }


const EditGiustificativoModal = ({ 
        selectedJustification, 
        selectedDescription, 
        selectedEmployee, 
        justificationId, 
        closeModalEditGiustificativi, 
        updateJustification, 
        onDeleteRequest,
        onReject,
    }) => {

    const cancelButtonRef = useRef(null);
    const { loading, token, profile } = useContext(AuthContext);

    if (!selectedEmployee || !selectedJustification) return null;

    const justificationToEdit = selectedJustification.justifications.find((justification) => justification.id === justificationId);

    const endData = formatJustificationEnd(justificationToEdit?.datetime_from, justificationToEdit?.datetime_to);

    const [startTime, setStartTime] = useState(formatTime(justificationToEdit?.datetime_from) || '');
    const [endTime, setEndTime] = useState(endData.time || '');
    const [startDay, setStartDay] = useState(formatDate(justificationToEdit?.datetime_from) || '');
    const [endDay, setEndDay] = useState(endData.date || '');
    const [status, setStatus] = useState(justificationToEdit?.status || '');
    const [note, setNote] = useState(justificationToEdit?.note || '');
    const [timeError, setTimeError] = useState('');
    const [isInputReset, setIsInputReset] = useState(false);
    
    function formatDate(dateString) {
        return moment(dateString).format('DD/MM/YYYY');
    }
    
    const handleWorkTimeBlur = (field) => {
        let formattedTime = field === 'start' ? startTime : endTime;
      
        formattedTime = formattedTime.replace(/\D/g, '');
      
        while (formattedTime.length < 4) {
          formattedTime += '0';
        }
      
        formattedTime = formattedTime.slice(0, 4);
      
        const hours = formattedTime.slice(0, 2);
        const minutes = formattedTime.slice(2);
        formattedTime = `${hours}:${minutes}`;
      
        const isValidTime = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formattedTime);
      
        if (!isValidTime) {
          if (field === 'start') {
            setStartTime('');
          } else {
            setEndTime('');
          }
          return;
        }
      
        if (field === 'start') {
          setStartTime(formattedTime);
        } else {
          setEndTime(formattedTime);
        }
    };

    const handleStartTimeChange = (e) => {
        let inputValue = e.target.value;
        inputValue = inputValue.replace(/\D/g, '');
        inputValue = inputValue.slice(0, 4);
      
        let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
      
        if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
          formattedTime = formattedTime.replace(":", "");
        }
      
        setStartTime(formattedTime);
    
        if (endTime && formattedTime >= endTime) {
          setTimeError("L'orario di inizio deve essere precedente all'orario di fine.");
        } else {
          setTimeError('');
        }
    };
      
    
    const handleEndTimeChange = (e) => {
        let inputValue = e.target.value;
      
        inputValue = inputValue.replace(/\D/g, '');
        inputValue = inputValue.slice(0, 4);
      
        let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
      
        if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
          formattedTime = formattedTime.replace(":", "");
        }
      
        setEndTime(formattedTime);
      
        if (startTime && formattedTime <= startTime) {
          setTimeError("L'orario di fine deve essere successivo all'orario di inizio.");
        } else {
          setTimeError('');
        }
    };

    const handleKeyDown = (e) => {
        const { name } = e.target;

        if (!isInputReset) {
          if (name === 'startDay') {
            setStartDay('');
          } else if (name === 'endDay') {
            setEndDay('');
          }
          setIsInputReset(true);
        }
    };
      
    const handleBlur = () => {
        setIsInputReset(false);
    };

    const handleStartDayChange = (e) => {
        let inputValue = e.target.value;
    
        inputValue = inputValue.replace(/\D/g, '');
    
        if (inputValue.length > 2 && inputValue.length <= 4) {
            inputValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2)}`;
        } else if (inputValue.length > 4) {
            inputValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2, 4)}/${inputValue.slice(4)}`;
        }
    
        setStartDay(inputValue);
    
        if (endDay && inputValue.length === 10) {
            const [startDay, startMonth, startYear] = inputValue.split('/');
            const startDate = new Date(`${startYear}-${startMonth}-${startDay}`);
    
            const [endDay, endMonth, endYear] = endDay.split('/');
            const endDate = new Date(`${endYear}-${endMonth}-${endDay}`);
    
            if (startDate >= endDate) {
                setTimeError("La data di inizio deve essere precedente alla data di fine.");
            } else {
                setTimeError('');
            }
        }
    };
    
    
    
    const handleEndDayChange = (e) => {
        let inputValue = e.target.value;
        
        inputValue = inputValue.replace(/\D/g, '');
        
        if (inputValue.length > 2 && inputValue.length <= 4) {
            inputValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2)}`;
        } else if (inputValue.length > 4) {
            inputValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2, 4)}/${inputValue.slice(4)}`;
        }
        
        setEndDay(inputValue);
        
        if (startDay && inputValue.length === 10) {
            const [startDayPart, startMonth, startYear] = startDay.split('/');
            const startDate = new Date(`${startYear}-${startMonth}-${startDayPart}`);
            
            const [endDayPart, endMonth, endYear] = inputValue.split('/');
            const endDate = new Date(`${endYear}-${endMonth}-${endDayPart}`);
            
            if (endDate <= startDate) {
                setTimeError("La data di fine deve essere successiva alla data di inizio.");
            } else {
                setTimeError('');
            }
        }
    };
    

    const handleNoteChange = (event) => {
        setNote(event.target.value);
    };
      
    const convertToISOFormat = (dateString) => {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month}-${day}`;
    };

    const handleConfirmUpdateJustification = async () => {
        try {
            const startISO = convertToISOFormat(startDay) + `T${startTime}:00`;
            const endISO = convertToISOFormat(endDay) + `T${endTime}:00`;
    
            await updateJustificationRequestStatus(token, justificationId, undefined, undefined, startISO, endISO, note);
    
            await Swal.fire({
                icon: 'success',
                title: 'Salvato!',
                text: 'Il giustificativo è stato modificato con successo',
                showConfirmButton: false,
                timer: 1000,
            });
    
            closeModalEditGiustificativi();
            updateJustification({ justificationId, startISO, endISO, note, employee: selectedEmployee })

        } catch (error) {
            console.error('Failed to update justification:', error);
    
            let errorMessage = 'Si è verificato un errore durante la modifica del giustificativo. Per favore riprova.';
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorMessages = [];
    
                Object.keys(errorData).forEach(key => {
                    const messageArray = errorData[key];
                    if (Array.isArray(messageArray)) {
                        errorMessages.push(...messageArray);
                    }
                });
    
                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(' ');
                }
            }
    
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: errorMessage,
                confirmButtonText: 'OK',
            });
        }
    };

    const handleRejectJustificationRequest = async () => {
        try {
            const confirmReject = await Swal.fire({
                icon: 'warning',
                title: 'Conferma rifiuto richiesta',
                text: 'Sei sicuro di voler rifiutare questa richiesta di giustificativo? Se cambi idea potrai modificare l\'esito della richiesta nella sezione Richieste Giustificativi Elaborate',
                showCancelButton: true,
                confirmButtonText: 'Conferma',
                cancelButtonText: 'Annulla',
                cancelButtonColor: '#d1d5db',
            });
    
            if (!confirmReject.isConfirmed) {
                return;
            }
    
            await updateJustificationRequestStatus(token, justificationId, 'rejected');
    
            await Swal.fire({
                icon: 'success',
                title: 'Salvato!',
                text: 'Richiesta rifiutata',
                showConfirmButton: false,
                timer: 1500,
                allowEscapeKey: false,
                allowOutsideClick: false,
            });
    
            closeModalEditGiustificativi();
            updateJustification({ justificationId, status: 'rejected', employee: selectedEmployee });
    

    
        } catch (error) {
            console.error('Failed to reject justification request:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Si è verificato un errore durante il rifiuto della richiesta. Per favore riprova.',
                confirmButtonText: 'OK',
            });
        }
    };
    

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 overflow-y-auto top-10" style={{ zIndex: '150' }} onClose={() => {}} initialFocus={cancelButtonRef}>
                <div className="flex items-start justify-center min-h-screen px-4 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                    </Transition.Child>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-2/3 sm:w-1/3 mt-20 sm:mt-30 top-0">
                            <div className="pb-2 pt-3 border-b border-gray-200 text-center flex items-center justify-center bg-indigo-200">
                                <PencilSquareIcon className="h-5 w-5 text-indigo-700" /><span className='w-4'></span>
                                <Dialog.Title as="h3" className="text-lg font-semibold text-indigo-700">
                                    Modifica Giustificativo
                                </Dialog.Title>
                            </div>

                            <div className='p-4'>
                                <div className='grid grid-cols-2 gap-2 w-full p-2'>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dipendente
                                    </div>
                                    <div className='col-span-1 text-sm flex items-center text-gray-600 text-sm'>
                                        {selectedEmployee.last_name && selectedEmployee.first_name ? `${selectedEmployee.last_name} ${selectedEmployee.first_name}` : selectedEmployee.cf}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Fasce orarie lavoro
                                    </div>
                                    <div className='col-span-1 text-sm flex-col items-center text-gray-600 text-sm'>
                                        {selectedJustification && selectedJustification.time_slots && selectedJustification.time_slots.length > 0 ? (
                                            selectedJustification && selectedJustification.time_slots && selectedJustification.time_slots.map((timeSlot, index) => (
                                                <div key={index} className="mb-2 flex items-center">
                                                    <div>
                                                        <div className='flex items-center text-xs'>{timeSlot.start_at} - {timeSlot.end_at}</div>
                                                        {timeSlot.breaks && timeSlot.breaks.length > 0 && (
                                                            <ul>
                                                                {timeSlot.breaks.map((breakItem, breakIndex) => (
                                                                    <li className='text-xs' key={breakIndex}>
                                                                        Pausa {breakItem.start_at} - {breakItem.end_at}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-sm text-gray-600">Nessun orario previsto</div>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dal giorno
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justificationToEdit && justificationToEdit.datetime_from ? (
                                            <div className="text-sm text-gray-600">{startDay}</div>
                                         
                                                                             
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Al giorno
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justificationToEdit && justificationToEdit.datetime_to? (
                                            <div className="text-sm text-gray-600">{endDay}</div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>

                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Giustificativo
                                    </div>
                                    <div className='col-span-1 text-sm flex items-center font-semibold text-indigo-600 uppercase'>
                                        {selectedDescription}
                                    </div>

                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dalle ore
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justificationToEdit && justificationToEdit.datetime_from ? (
                                            <div className="text-sm text-gray-600">{startTime}</div>           
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Alle ore
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justificationToEdit && justificationToEdit.datetime_to ? (
                                            <div className="text-sm text-gray-600">{endTime}</div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Note
                                    </div>
                                    <div className='col-span-1 text-sm'>
                                        {justificationToEdit && justificationToEdit.note ? (
                                            <div className="text-sm text-gray-600">{note}</div>
                                        ) : (
                                            <span></span>
                                        )}
                                    </div> 
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Unità associata
                                    </div>
                                    <div className='col-span-1 text-sm'>
                                        {justificationToEdit && justificationToEdit?.ou_name ? (
                                            <div className="text-sm text-gray-600">{justificationToEdit.ou_name}</div>
                                        ) : (
                                            <span className='text-sm text-gray-600'>Nessuna</span>
                                        )}
                                    </div> 
                       

                                    {/* <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dal giorno
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justificationToEdit && justificationToEdit.datetime_from ? (
                                            <div className="col-span-1 text-sm items-center" style={{ position: 'relative' }}>
                                                <input 
                                                    name='startDay' 
                                                    type="text"
                                                    placeholder='ggmmaaaa' 
                                                    value={startDay || ''}
                                                    onChange={handleStartDayChange}
                                                    onKeyDown={handleKeyDown}
                                                    onBlur={handleBlur}
                                                    maxLength={10}
                                                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 block w-full shadow-md sm:text-sm required" 
                                                />
                                            </div>                                        
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Al giorno
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justificationToEdit && justificationToEdit.datetime_to ? (
                                            <div className="col-span-1 text-sm items-center" style={{ position: 'relative' }}>
                                                <input 
                                                    name='endDay'
                                                    type="text"
                                                    placeholder='ggmmaaaa' 
                                                    value={endDay || ''}
                                                    onInput={handleEndDayChange}
                                                    onKeyDown={handleKeyDown}
                                                    onBlur={handleBlur}
                                                    maxLength={10}
                                                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 block w-full shadow-md sm:text-sm required" 
                                                />
                                            </div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Giustificativo
                                    </div>
                                    <div className='col-span-1 text-sm flex items-center font-semibold text-gray-600' style={{ textDecoration: 'underline', textDecorationColor: '#6d28d9', textDecorationThickness: '1px' }}>
                                        {selectedDescription}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Status
                                    </div> 
                                    {status === 'approved' && (
                                    <div className='col-span-1 text-sm flex items-center font-semibold text-gray-600' style={{ textDecoration: 'underline', textDecorationColor: '#6d28d9', textDecorationThickness: '1px' }}>
                                        Approvata
                                    </div>
                                    )}
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dalle ore
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justificationToEdit && justificationToEdit.datetime_from ? (
                                            <div className="col-span-1 text-sm items-center" style={{ position: 'relative' }}>
                                                <input 
                                                type="text"
                                                placeholder='hh:mm' 
                                                value={startTime} 
                                                onInput={handleStartTimeChange}
                                                onBlur={() => handleWorkTimeBlur('start')}
                                                className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 block w-full shadow-md sm:text-sm required" 
                                                style={{ paddingRight: '2.5rem' }}
                                                />
                                            <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                                <ClockIcon className='w-4 h-4' />
                                            </span>
                                            </div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Alle ore
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justificationToEdit && justificationToEdit.datetime_to ? (
                                            <div className='col-span-1 text-sm items-center' style={{ position: 'relative' }}>
                                                <input 
                                                    type="text"
                                                    placeholder='hh:mm' 
                                                    value={endTime} 
                                                    onInput={handleEndTimeChange} 
                                                    onBlur={() => handleWorkTimeBlur('end')}
                                                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 block w-full shadow-md sm:text-sm required" 
                                                    style={{ paddingRight: '2.5rem' }}
                                                />
                                                <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                                    <ClockIcon className='w-4 h-4' />
                                                </span>
                                            </div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Note:
                                    </div>
                                    <div className='col-span-1 text-sm h-20'>
                                        {justificationToEdit ? (
                                            <textarea
                                                name="note"
                                                value={note}
                                                onChange={handleNoteChange}
                                                className="w-full h-full border-gray-200 shadow-md rounded text-sm focus:ring-indigo-500"
                                            ></textarea>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div> */}
                                </div>
                                <div className="mt-5 mb-2 sm:mt-6 flex justify-center">
                                    {/* <button
                                        type="button"
                                        className="hover:bg-indigo-100 w-1/2 text-xs flex justify-center items-center border border-gray-300 focus:ring-indigo-500 shadow-md rounded-md text-gray-500 font-bold btn-xs p-2 mr-4"
                                        onClick={handleConfirmUpdateJustification}
                                        ref={cancelButtonRef}
                                    >
                                        <CheckIcon className="h-4 w-4 mr-1 text-indigo-700" />
                                        SALVA MODIFICHE
                                    </button> */}
                                
                                {profile?.customer_type !== 'employee' || 
                                    (profile?.employee?.permissions?.includes('hr.manage_justification_ou') || 
                                    profile?.employee?.permissions?.includes('hr.manage_justification_all')) ? (
                                    <>
                                        {justificationId && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="me-2 hover:bg-gray-100 w-1/2 text-xs flex justify-center items-center border border-gray-300 focus:ring-indigo-500 shadow-md rounded-md text-gray-500 font-bold btn-xs p-2"
                                                    onClick={handleRejectJustificationRequest}
                                                >
                                                    <ArrowPathIcon className="h-4 w-4 mr-1 text-red-500" />
                                                    RIFIUTA RICHIESTA
                                                </button>
                                                <button
                                                    type="button"
                                                    className="hover:bg-gray-100 w-1/2 text-xs flex justify-center items-center focus:ring-indigo-500 border border-gray-300 shadow-md rounded-md text-gray-500 font-bold btn-xs p-2 mr-4"
                                                    onClick={() => onDeleteRequest(justificationId, selectedEmployee.id, justificationToEdit, selectedEmployee)}
                                                >
                                                    <TrashIcon className="h-4 w-4 mr-1 text-red-500" />
                                                    ELIMINA
                                                </button>
                                            </>
                                        )}
                                    </>
                                ) : null}
                                </div>
                                <div className="mt-5 mb-2 sm:mt-6 flex justify-center">
                                  
                                    <button
                                        type="button"
                                        className="hover:bg-gray-100 w-1/2 text-xs flex justify-center focus:ring-indigo-500 items-center border border-gray-300 shadow-md rounded-md text-gray-500 font-bold btn-xs p-2"
                                        onClick={closeModalEditGiustificativi}
                                        ref={cancelButtonRef}
                                    >
                                        ANNULLA
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default EditGiustificativoModal;
